import type { PipeInterface } from '@render/composables/usePipe'

/**
 * Local wall-clock timestamp — deliberately NOT UTC/ISO. The point is to
 * capture what the device's own clock read at the moment of the action, so
 * the backend can read the hour straight off the string (e.g. `orderCreation`
 * feeds the `leadEmailSubmissionLocalHour` Klaviyo lead-event property — see
 * KlaviyoEventsService.createLeadEmailEvent) with no timezone math on either
 * side.
 */
export const formatLocalDeviceTime = (date: Date = new Date()): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  )
}

// Grouped device-local moment-in-time captures, one object under a single
// var.global.deviceTimestamps key — same pattern as var.global.usersIPs in
// pipeorders.ts. Lives here (not in an owning integration) because pipeorders,
// Primer and Yuno all write into it — no single integration owns every
// capture site, so no integration should own the type or the setter either.
export type DeviceTimestamps = {
  // Landing-page moment — when this visit/session first loaded the pipe.
  // Per-visit, unlike the rest: an abandoned-cart resume must NOT restore
  // this from the original order (see pipeorders.ts onReady's
  // deviceTimestamps merge) or it would read as the visitor's first visit.
  sessionStarted?: string | null
  leadEmailSubmission?: string | null
  orderCreation?: string | null
  // Set when the visitor submits payment (SUBMIT_CC_INPUT status transition).
  paymentAttempted?: string | null
  // Set right before the client asks payments-api to create a payment
  // session — written from both usePrimer.ts and the Yuno session-create
  // sites (yunoPreCreate.ts, useYuno.ts), whichever fires first.
  paymentSessionCreated?: string | null
  // Set once the checkout genuinely completes a purchase — inside each
  // component's onCheckoutComplete, gated behind the same `isPpoPurchased`
  // check that guards recordPurchase/emitPayment, so a replayed callback or a
  // shouldSkip/back-navigation revisit (already purchased) never re-fires it.
  // Written from both PrimerCheckout.vue and YunoCheckout.vue.
  paymentCompleted?: string | null
}

// Sets one key of var.global.deviceTimestamps to "now", but only the first
// time — guarded on the key already being present so a retry / hard reload
// never overwrites the original moment with a later one.
export const setDeviceTimestampOnce = (p: PipeInterface, key: keyof DeviceTimestamps): void => {
  const existing = (p.getData('var.global.deviceTimestamps') as DeviceTimestamps | null) ?? {}
  if (existing[key]) return
  p.setData('var.global.deviceTimestamps', { ...existing, [key]: formatLocalDeviceTime() })
}
